import { Button, DatePicker, FormField, Textarea, todayIso, useToast } from '@ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { isQueued } from '../../offline/offline';
import { photoUrl, useDeleteNote, usePhoto, useSaveNote, type Note } from '../api';
import styles from './forms.module.css';

export function NoteDialog({
  open,
  onClose,
  colonyId,
  note,
}: {
  open: boolean;
  onClose: () => void;
  colonyId: string;
  note?: Note;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveNote();
  const remove = useDeleteNote();
  const upload = usePhoto('colony-notes');
  const picker = useRef<HTMLInputElement>(null);
  // A photo chosen now, or the wish to drop the existing one.
  const [file, setFile] = useState<File | null>(null);
  const [dropPhoto, setDropPhoto] = useState(false);
  // The note saved by an earlier submit whose photo failed: a retry updates it.
  const [savedId, setSavedId] = useState<string | undefined>(undefined);
  const hasPhoto = Boolean(file) || (Boolean(note?.has_photo) && !dropPhoto);
  const [text, setText] = useState(note?.text ?? '');
  const [on, setOn] = useState(note?.noted_on ?? todayIso());
  const errors = formErrors(save.error ?? remove.error ?? upload.error, { text: ['note_text.'] });
  const close = () => {
    save.reset();
    remove.reset();
    onClose();
  };
  return (
    <FormDialog
      open={open}
      onClose={close}
      title={note ? t('apiary.note.editTitle') : t('apiary.note.addTitle')}
      submitLabel={t('common.save')}
      pending={save.isPending || upload.isPending}
      error={errors.general}
      extraActions={
        note && (
          <Button
            variant="ghost"
            loading={remove.isPending}
            onClick={() =>
              remove.mutate(note.id, {
                onSuccess: () => {
                  toast.show(t('apiary.note.deleted'));
                  close();
                },
              })
            }
          >
            {t('apiary.timeline.delete')}
          </Button>
        )
      }
      onSubmit={() =>
        save.mutate(
          { colonyId, id: note?.id ?? savedId, body: { noted_on: on, text } },
          {
            onSuccess: async (saved) => {
              if (isQueued(saved)) {
                // Captured offline: there is no note to attach a photo to yet.
                toast.show(t(file ? 'apiary.note.photoOffline' : 'offline.captured'), 'info');
                close();
                return;
              }
              const id = (saved as { id: string }).id;
              setSavedId(id);
              try {
                if (file) await upload.mutateAsync({ id, file });
                else if (dropPhoto && note?.has_photo) await upload.mutateAsync({ id, file: null });
              } catch {
                return; // the error shows in the dialog, the note itself is saved
              }
              toast.show(t('apiary.note.saved'));
              close();
            },
          },
        )
      }
    >
      <FormField label={t('apiary.note.date')}>
        {(p) => <DatePicker {...p} value={on} max={todayIso()} onChange={setOn} />}
      </FormField>
      <FormField label={t('apiary.note.text')} required error={errors.field.text}>
        {(p) => (
          <Textarea
            {...p}
            rows={5}
            value={text}
            maxLength={4000}
            onChange={(e) => setText(e.target.value)}
          />
        )}
      </FormField>
      <FormField label={t('apiary.note.photo')}>
        {() => (
          <div className={styles.photoField}>
            {note?.has_photo && !dropPhoto && !file && (
              <img
                className={styles.photoPreview}
                src={photoUrl(`/colony-notes/${note.id}/photo`, note.version)}
                alt={t('apiary.note.photoAlt')}
              />
            )}
            {file && <span>{file.name}</span>}
            <input
              ref={picker}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              hidden
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setDropPhoto(false);
                e.target.value = '';
              }}
            />
            <div className={styles.photoActions}>
              <Button variant="secondary" size="sm" onClick={() => picker.current?.click()}>
                {hasPhoto ? t('apiary.note.changePhoto') : t('apiary.note.addPhoto')}
              </Button>
              {hasPhoto && (
                <Button
                  variant="plain"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    setDropPhoto(true);
                  }}
                >
                  {t('apiary.note.removePhoto')}
                </Button>
              )}
            </div>
          </div>
        )}
      </FormField>
    </FormDialog>
  );
}
